import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAgendamentosRealtimeUnificado } from "@/hooks/useAgendamentosRealtimeUnificado";
import { Calendar, Clock, User, Briefcase, CheckCircle, XCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
const getStatusColors = (status: string) => {
  switch (status) {
    case 'AGENDADO':
      return "bg-green-500/20 border-green-500/40 text-green-500 shadow-[0_0_15px_rgba(34,197,94,0.15)]";
    case 'REAGENDADO':
      return "bg-amber-500/20 border-amber-500/40 text-amber-500 shadow-[0_0_15px_rgba(245,158,11,0.15)]";
    case 'CANCELADO':
      return "bg-red-500/20 border-red-500/40 text-red-500 shadow-[0_0_15px_rgba(239,68,68,0.15)]";
    default:
      return "bg-gray-500/20 border-gray-500/40 text-gray-400";
  }
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString + 'T00:00:00');
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const formatTime = (timeString: string) => {
  return timeString.slice(0, 5); // Remove segundos se existirem
};

export const PainelAgendamentos = () => {
  const { toast } = useToast();
  const {
    agendamentos,
    isLoading,
    error
  } = useAgendamentosRealtimeUnificado();

  const [isSubmitting, setIsSubmitting] = useState<Set<string>>(new Set());
  const [finalizados, setFinalizados] = useState<Set<string>>(new Set());
  const [storeInfo, setStoreInfo] = useState<{
    opening_time: string;
    closing_time: string;
    slot_interval_minutes: number;
    profissionais: string[];
  } | null>(null);

  useEffect(() => {
    const fetchStore = async () => {
      const { data } = await supabase.from('info_loja_public').select('*').limit(1).maybeSingle();
      if (data) {
        setStoreInfo({
          opening_time: data.opening_time,
          closing_time: data.closing_time,
          slot_interval_minutes: data.slot_interval_minutes || 30,
          profissionais: data.nome_profissionais 
            ? data.nome_profissionais.split(/[\n,]/).map((p: string) => p.trim()).filter(Boolean)
            : ['Profissional 1']
        });
      }
    };
    fetchStore();
  }, []);

  const timeSlots = useMemo(() => {
    if (!storeInfo) return [];
    const slots = [];
    let current = new Date(`2000-01-01T${storeInfo.opening_time}`);
    const end = new Date(`2000-01-01T${storeInfo.closing_time}`);
    
    while (current <= end) {
      slots.push(current.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }));
      current = new Date(current.getTime() + storeInfo.slot_interval_minutes * 60000);
    }
    return slots;
  }, [storeInfo]);

  // Obter data de hoje no formato YYYY-MM-DD
  const hoje = new Date();
  const year = hoje.getFullYear();
  const month = String(hoje.getMonth() + 1).padStart(2, '0');
  const day = String(hoje.getDate()).padStart(2, '0');
  const dataHoje = `${year}-${month}-${day}`;

  const agendamentosValidos = agendamentos.filter(ag => {
    const temFinalizacao = (ag.finalização && ag.finalização.trim() !== '') || finalizados.has(ag.id);
    return !temFinalizacao;
  });

  const agendamentosHoje = agendamentosValidos.filter(ag => ag.DATA === dataHoje);
  const agendamentosPassados = agendamentosValidos.filter(ag => ag.DATA < dataHoje && ag.STATUS !== 'CANCELADO');

  const handleStatusFinal = async (agendamentoId: string, statusFinal: string) => {
    setIsSubmitting(prev => new Set(prev).add(agendamentoId));
    try {
      const { error } = await supabase
        .from('agendamentos_robustos')
        .update({ finalização: statusFinal })
        .eq('id', agendamentoId);

      if (error) throw error;

      setFinalizados(prev => new Set(prev).add(agendamentoId));

      toast({
        title: "Status atualizado",
        description: `Agendamento marcado como ${statusFinal.toLowerCase()}`,
      });
    } catch (error) {
      console.error('Erro ao atualizar status final:', error);
      toast({
        title: "Erro",
        description: "Não foi possível atualizar o status do agendamento",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(prev => {
        const next = new Set(prev);
        next.delete(agendamentoId);
        return next;
      });
    }
  };

  const renderAgendamentoCard = (agendamento: any) => {
    const colors = getStatusColors(agendamento.STATUS);
    
    return (
      <div key={agendamento.id} className={`flex flex-col gap-2 p-3 rounded-lg border ${colors} h-full min-h-[140px] transition-all`}>
        <div className="flex justify-between items-start gap-2">
          <div className="font-bold text-sm leading-tight text-white">{agendamento.NOME}</div>
          <div className="text-[10px] font-black uppercase tracking-wider opacity-80 shrink-0 mt-0.5">{agendamento.STATUS}</div>
        </div>
        
        <div className="flex flex-col gap-1 text-xs opacity-90 mt-1">
          <div className="flex items-center gap-1"><Briefcase className="w-3 h-3" /> <span className="truncate">{agendamento.servico || agendamento.SERVICO || 'Não informado'}</span></div>
          <div className="flex items-center gap-1"><User className="w-3 h-3" /> <span className="truncate">{agendamento.CONTATO}</span></div>
          {agendamento.senha && (
            <div className="font-mono text-white bg-black/20 px-1.5 py-0.5 rounded w-fit mt-1">Senha: {agendamento.senha}</div>
          )}
        </div>
        
        <div className="mt-auto pt-2 flex items-center justify-between gap-1">
          {(agendamento.STATUS === 'AGENDADO' || agendamento.STATUS === 'REAGENDADO') && (
            <>
              <Button
                size="sm"
                onClick={() => handleStatusFinal(agendamento.id, 'EFETIVADO')}
                disabled={isSubmitting.has(agendamento.id)}
                className="flex-1 bg-green-600/80 hover:bg-green-500 text-white text-[10px] h-7 px-0"
              >
                <CheckCircle className="h-3 w-3 mr-1" /> EFETIVADO
              </Button>
              <Button
                size="sm"
                onClick={() => handleStatusFinal(agendamento.id, 'NÃO EFETIVADO')}
                disabled={isSubmitting.has(agendamento.id)}
                className="flex-1 bg-red-600/80 hover:bg-red-500 text-white text-[10px] h-7 px-0"
              >
                <XCircle className="h-3 w-3 mr-1" /> N.E
              </Button>
            </>
          )}
        </div>
      </div>
    );
  };

  if (error) {
    return (
      <div className="p-6">
        <h1 className="text-3xl font-bold text-primary mb-6">Agenda do Dia</h1>
        <div className="text-red-400 bg-red-500/10 border border-red-400/30 rounded-lg p-4">
          Erro ao carregar agendamentos: {error}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 bg-card/40 backdrop-blur-sm border border-white/5 rounded-2xl shadow-2xl">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-black text-primary flex items-center gap-3">
            <Calendar className="h-8 w-8 text-primary" />
            AGENDA DO DIA
          </h1>
          <p className="text-muted-foreground text-sm font-medium mt-1">Acompanhamento e finalização de horários ({formatDate(dataHoje)})</p>
        </div>
      </div>

      {isLoading || !storeInfo ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground font-medium">Carregando horários...</p>
        </div>
      ) : (
        <div className="space-y-8">
          {/* Grade de Hoje */}
          <div className="overflow-x-auto pb-4">
            <div className="min-w-[800px]">
              {/* Cabeçalho de Profissionais */}
              <div className="grid border-b border-white/10 pb-4 mb-4" style={{ gridTemplateColumns: `80px repeat(${storeInfo.profissionais.length}, minmax(250px, 1fr))` }}>
                <div className="font-bold text-muted-foreground text-sm flex items-center justify-center">HORA</div>
                {storeInfo.profissionais.map((prof) => (
                  <div key={prof} className="font-black text-lg text-center text-primary uppercase tracking-wider">{prof}</div>
                ))}
              </div>

              {/* Linhas de Horário */}
              <div className="flex flex-col gap-3">
                {timeSlots.map((time) => (
                  <div key={time} className="grid items-stretch gap-4" style={{ gridTemplateColumns: `80px repeat(${storeInfo.profissionais.length}, minmax(250px, 1fr))` }}>
                    {/* Coluna do Tempo */}
                    <div className="flex items-start justify-center pt-2">
                      <Badge variant="outline" className="font-bold text-sm bg-zinc-900/80 border-white/10">{time}</Badge>
                    </div>

                    {/* Colunas dos Profissionais */}
                    {storeInfo.profissionais.map((prof) => {
                      // Busca agendamentos neste horário exato para este profissional
                      // Considera correspondência parcial de nome
                      const agsNoHorario = agendamentosHoje.filter(ag => 
                        formatTime(ag.HORA) === time && 
                        ag.PROFISSIONAL && 
                        ag.PROFISSIONAL.toLowerCase().includes(prof.split(' ')[0].toLowerCase())
                      );

                      if (agsNoHorario.length > 0) {
                        return (
                          <div key={`${time}-${prof}`} className="flex flex-col gap-2">
                            {agsNoHorario.map(ag => renderAgendamentoCard(ag))}
                          </div>
                        );
                      } else {
                        return (
                          <div key={`${time}-${prof}`} className="h-12 border border-dashed border-white/5 bg-white/[0.02] rounded-lg flex items-center justify-center text-white/20 text-xs font-medium">
                            Livre
                          </div>
                        );
                      }
                    })}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Pendências de Dias Anteriores */}
          {agendamentosPassados.length > 0 && (
            <div className="mt-12 pt-8 border-t border-white/10">
              <h2 className="text-lg font-bold text-amber-500 flex items-center gap-2 mb-4">
                <Clock className="w-5 h-5" />
                PENDÊNCIAS ANTERIORES ({agendamentosPassados.length})
              </h2>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {agendamentosPassados.map(ag => (
                  <div key={ag.id} className="relative">
                    <div className="absolute -top-3 -right-2 z-10 bg-zinc-900 border border-amber-500/30 text-amber-500 text-[10px] font-bold px-2 py-0.5 rounded-full">
                      {formatDate(ag.DATA)}
                    </div>
                    {renderAgendamentoCard(ag)}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};